import React, { useEffect, useRef, useState } from 'react';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { featuresStepValidations } from './validations';
import { yupResolver } from '@hookform/resolvers';
import { useForm } from 'react-hook-form';
import {
  getFeatures,
  updateFormValues,
  updateUnitMeasurement,
  updateAntiquityType
} from '../../../ducks/features/actions';
import { FeaturesSelectors } from '../../../ducks/features';
import { getStepMainSelectors } from '../../../ducks/propertyType/index';
import { useLocation } from 'react-router-dom';
import { usePreventWindowUnload, useQuery } from '@panel/commons/hooks';
import StepSectionWrapper from '../../commons/StepSectionWrapper';
import InputWithTypeSelector from '../../commons/InputWithTypeSelector';
import { AreaContainer } from './styles';
import { getDevice, site } from '@panel/commons/helpers';
import AntiquitySection from './AntiquitySection';
import RoomsSection from './RoomsSection';
import {
  featuresWithAllowedZeroValue,
  getAntiquityNotApply,
  getConfigByRealEstateType,
  getCoveredAreaNotApply,
  getIsOptionalAntiquity,
  getIsOptionalCoveredArea,
  getIsOptionalTotalArea,
  mainFeaturesIds,
  roomsOptionsConfig
} from './featuresConfig';
import { bodyManagerFeaturesStep } from '../../../utils/bodyManagerFeaturesStep';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { StepperOperations, StepperSelectors } from '../../../ducks/stepper';
import { resetInfo } from '../../../ducks/propertyType/actions';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { transformValue } from '../../../utils/numberFormatHelper';

const Features = () => {
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const [postingId] = useQuery(useLocation(), ['postingId']);
  const { t } = useTranslation();
  const mainData = useSelector(getStepMainSelectors);
  const { snackAction } = useSelector(StepperSelectors.getStepperState);
  const { setSnackAction } = StepperOperations;
  const { formValues, unitMeasurement, antiquityType, fetchCompleted, hasSavedData, loading, fetchError } = useSelector(
    FeaturesSelectors.getFeaturesState
  );
  const isOptionalTotalArea = getIsOptionalTotalArea(mainData?.formValues.propertyType);
  const isOptionalCoveredArea = getIsOptionalCoveredArea(mainData?.formValues.propertyType);
  const areaMeasurementTypes = [
    { value: '1', label: t('professionalPubli.features.area.unit.m') },
    { value: '2', label: t('professionalPubli.features.area.unit.h') }
  ];
  const [optionsByRealEstateType, setOptionsByRealEstateType] = useState([]);
  const { register, handleSubmit, errors, setValue, clearErrors, formState, setError, trigger, watch } = useForm({
    defaultValues: {
      ...formValues
    },
    context: { realEstateTypeId: mainData?.formValues.propertyType },
    mode: 'onSubmit',
    shouldFocusError: true,
    resolver: yupResolver(featuresStepValidations),
    reValidateMode: 'onChange'
  });
  const coveredArea = watch('CFT101');
  const totalArea = watch('CFT100');
  const isMobile = getDevice() === 'mobile';
  const { setFormIsDirty } = useFormIsDirty();
  usePreventWindowUnload(formState.isDirty);

  // Sincronizar valores de superficie con Redux
  useEffect(() => {
    dispatch(updateFormValues({ [mainFeaturesIds.SUP_TOTAL]: totalArea }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalArea]);

  useEffect(() => {
    dispatch(updateFormValues({ [mainFeaturesIds.SUP_CUBIERTA]: coveredArea }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coveredArea]);

  useEffect(() => {
    if (postingId && !fetchError) {
      dispatch(getFeatures(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId]);

  const updateCFTValue = (value, CFT) => {
    clearErrors(CFT);
    setValue(CFT, value, { shouldDirty: true });
    dispatch(updateFormValues({ [CFT]: value }));
  };

  useEffect(() => {
    // cargo los valores guardados en la BD para los inputs
    if (fetchCompleted && mainData.formValuesCompleted) {
      if (hasSavedData) {
        if (formValues[mainFeaturesIds.SUP_CUBIERTA] !== '') {
          setValue(
            mainFeaturesIds.SUP_CUBIERTA,
            transformValue(formValues[mainFeaturesIds.SUP_CUBIERTA], { shouldDirty: true })
          );
        }
        if (formValues[mainFeaturesIds.SUP_TOTAL] !== '') {
          setValue(
            mainFeaturesIds.SUP_TOTAL,
            transformValue(formValues[mainFeaturesIds.SUP_TOTAL], { shouldDirty: true })
          );
        }
        if (formValues[mainFeaturesIds.HABITACIONES] !== 0) {
          setValue(mainFeaturesIds.HABITACIONES, formValues[mainFeaturesIds.HABITACIONES], { shouldDirty: true });
        }
        if (formValues[mainFeaturesIds.BANOS] !== 0) {
          setValue(mainFeaturesIds.BANOS, formValues[mainFeaturesIds.BANOS], { shouldDirty: true });
        }
        if (formValues[mainFeaturesIds.MEDIOS_BANOS] !== 0) {
          setValue(mainFeaturesIds.MEDIOS_BANOS, formValues[mainFeaturesIds.MEDIOS_BANOS], { shouldDirty: true });
        }
        if (formValues[mainFeaturesIds.GARAGE] !== 0) {
          setValue(mainFeaturesIds.GARAGE, formValues[mainFeaturesIds.GARAGE], { shouldDirty: true });
        }
        if (formValues[mainFeaturesIds.AMBIENTES] !== 0 && site.isArgentina) {
          setValue(mainFeaturesIds.AMBIENTES, formValues[mainFeaturesIds.AMBIENTES], { shouldDirty: true });
        }
        const configByRealStateType = getConfigByRealEstateType(mainData.formValues?.propertyType);
        const options = [];
        roomsOptionsConfig.forEach(op => {
          const configByCFT = configByRealStateType[op.featureId];
          // La seunda condicion del IF es para que cuando hay un valor cargado en la BD de un aviso que se creo con el publi viejo
          // y se quiere editar con el nuevo permita ver el valor y modificarlo lo mismo ocurre en antiuedad y superficie construida
          if (configByCFT.apply || formValues[op.featureId] > 0) {
            options.push({ ...op, initialValue: configByCFT.isOptional ? 0 : 1, isOptional: configByCFT.isOptional });
          }
        });
        setOptionsByRealEstateType(options);
      } else {
        // si no hay nada en la base cargo los datos por defecto
        const configByRealStateType = getConfigByRealEstateType(mainData.formValues?.propertyType);
        const options = [];
        roomsOptionsConfig.forEach(op => {
          const configByCFT = configByRealStateType[op.featureId];
          if (configByCFT.apply) {
            options.push({ ...op, initialValue: configByCFT.isOptional ? 0 : 1, isOptional: configByCFT.isOptional });
            if (!formValues[op.featureId]) {
              updateCFTValue(configByCFT.isOptional ? 0 : 1, op.featureId);
            }
          }
        });
        // si es rancho id 14 entonces seteo el valor de la unidad de medida a 2
        // 2 = hectareas
        if (mainData.formValues?.propertyType === '14') {
          dispatch(updateUnitMeasurement('total', '2'));
          dispatch(updateUnitMeasurement('covered', '2'));
        }
        setOptionsByRealEstateType(options);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompleted, hasSavedData, mainData?.formValuesCompleted]);
  // }, [mainData, fetchCompleted, hasSavedData]);

  useEffect(() => {
    register(mainFeaturesIds.ANTIGUEDAD);
    register(mainFeaturesIds.HABITACIONES);
    register(mainFeaturesIds.BANOS);
    register(mainFeaturesIds.MEDIOS_BANOS);
    register(mainFeaturesIds.GARAGE);
    register(mainFeaturesIds.SUP_CUBIERTA);
    register(mainFeaturesIds.SUP_TOTAL);
    if (site.isArgentina) {
      register(mainFeaturesIds.AMBIENTES);
    }
  }, [register]);

  useEffect(() => {
    setFormIsDirty(formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.isDirty]);

  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  const validMeasurementUnits = () => {
    if (getCoveredAreaNotApply(mainData?.formValues.propertyType)) return true;
    // Si un campo es opcional y está vacío (<=0), no validamos las unidades
    if (isOptionalTotalArea && totalArea <= 0) return true;
    if (isOptionalCoveredArea && coveredArea <= 0) return true;
    // Si ambos tienen valores reales, validamos que las unidades coincidan
    return unitMeasurement.total === unitMeasurement.covered;
  };

  const validAntiquityType = () => {
    // para validar me fijo que aplique en el tipo de propiedad
    if (getIsOptionalAntiquity(mainData?.formValues.propertyType)) return true;
    return antiquityType != null;
  };

  const validMainFeatures = () => {
    let isValid = true;
    optionsByRealEstateType.forEach(op => {
      if (formValues[op.featureId] < op.initialValue) {
        isValid = false;
      }
    });
    return isValid;
  };

  useEffect(() => {
    const errorKeys = Object.keys(errors);
    if (
      errorKeys.some(key => {
        return (
          (key === mainFeaturesIds.SUP_TOTAL || key === mainFeaturesIds.SUP_CUBIERTA) &&
          errors[key].type.includes('empty')
        );
      })
    ) {
      dispatch(
        setSnackAction({
          action: 'agregarActionEmpty',
          result: 'ERROR',
          component: t('professionalPubli.features.area.snack.generic'),
          section: 'Publicador profesionales - Paso 4'
        })
      );
    }
    if (
      errorKeys.some(key => {
        return (
          key === mainFeaturesIds.ANTIGUEDAD &&
          (errors[key].type.includes('empty-required') || errors[key].type === 'typeError')
        );
      })
    ) {
      dispatch(
        setSnackAction({
          action: 'antiquityEmpty',
          result: 'ERROR',
          component: t('professionalPubli.features.antiquity.snack.error'),
          section: 'Publicador profesionales - Paso 4'
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const onSubmit = data => {
    // Creamos una copia de data para Redux (sin los 0 de los campos opcionales de superficie)
    const dataForRedux = { ...data };
    // Si un campo opcional de superficie es 0, lo convertimos a '' (así evitamos mostrar 0 al volver al paso y evitamos errores de cast de Yup)
    if (isOptionalTotalArea && dataForRedux[mainFeaturesIds.SUP_TOTAL] === 0) {
      dataForRedux[mainFeaturesIds.SUP_TOTAL] = '';
    }
    if (isOptionalCoveredArea && dataForRedux[mainFeaturesIds.SUP_CUBIERTA] === 0) {
      dataForRedux[mainFeaturesIds.SUP_CUBIERTA] = '';
    }
    dispatch(updateFormValues(dataForRedux));

    // validaciones del snackbar
    if (!validMeasurementUnits()) {
      return showErrorSnackbar('agregarActionEmpty', t('professionalPubli.features.area.snack.error'));
    }
    if (!validAntiquityType()) {
      return showErrorSnackbar('agregarActionEmpty', t('professionalPubli.features.antiquity.snack.error'));
    }
    if (!validMainFeatures()) {
      return showErrorSnackbar('agregarActionEmpty', t('professionalPubli.features.rooms.snack.error'));
    }
    // fin validaciones del snackbar
    if (data[mainFeaturesIds.ANTIGUEDAD] === '' && !getIsOptionalAntiquity(mainData?.formValues.propertyType)) {
      return setError(mainFeaturesIds.ANTIGUEDAD, {
        type: 'empty-required',
        message: 'professionalPubli.features.antiquity.snack.asistText.empty'
      });
    }
    // se filtran los atributos que no tienen valor
    const dataKeys = Object.keys(data);
    dataKeys.forEach(key => {
      if (data[key] === '' || data[key] === null) {
        delete data[key];
      }
    });
    const body = { ...bodyManagerFeaturesStep(data, unitMeasurement), postingId };
    const payload = {
      body,
      step: 'STEP_MAIN'
    };
    dispatch(SavePostingOperations.savePostingDB(payload));
  };

  // Snackbar de error para validaciones:
  const showErrorSnackbar = (actionKey, message) => {
    dispatch(
      setSnackAction({
        action: actionKey,
        result: 'ERROR',
        component: message,
        section: 'Publicador profesionales - Paso 4'
      })
    );
  };

  useEffect(() => {
    // Saneamiento del state que dispara el Snackbar:
    return () => {
      dispatch(setSnackAction({ action: '', result: '' }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snackAction.result]);

  const getUnitMeasurementName = value => {
    return areaMeasurementTypes.filter(m => m.value === value)[0]?.label;
  };

  return (
    <WrapperContainer
      id='features-step'
      getIsValidForm={getIsValidForm}
      title={t('professionalPubli.features.title')}
      note={t('professionalPubli.features.note')}
      onSubmit={handleSubmit(onSubmit)}
      btnSubmitText={t('professionalPubli.cta.continue')}
      formValuesLoading={loading}
      loadError={fetchError}
      resetInfo={() => dispatch(resetInfo())}
    >
      <form data-publi='featuresStep-form' id='featuresStep' ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <StepSectionWrapper
          title={t('professionalPubli.features.area.title')}
          dataPubli='professionalPubli-featuresStep-areaContainer'
        >
          <AreaContainer isMobile={isMobile}>
            <InputWithTypeSelector
              label={`${t('professionalPubli.features.area.total')}${isOptionalTotalArea ? '' : '*'}`}
              placeholder={t('professionalPubli.features.area.total.placeholder')}
              typesOptions={areaMeasurementTypes}
              name={mainFeaturesIds.SUP_TOTAL}
              width={isMobile ? '100%' : '125px'}
              error={t(errors[mainFeaturesIds.SUP_TOTAL]?.message)}
              onSelect={value => {
                dispatch(updateUnitMeasurement('total', value));
              }}
              currentSelectorValue={getUnitMeasurementName(unitMeasurement.total)}
              onInputChange={value => {
                const valueTransformed = transformValue(value);
                setValue(mainFeaturesIds.SUP_TOTAL, valueTransformed, { shouldDirty: true });
              }}
              currentValue={totalArea}
              dataPubli='professionalPubli-totalSelector'
              tooltipText={t('professionalPubli.features.area.total.tooltip')}
            />
            {!getCoveredAreaNotApply(mainData?.formValues.propertyType) || formValues['CFT101'] > 0 ? (
              <InputWithTypeSelector
                label={`${t('professionalPubli.features.area')}${isOptionalCoveredArea ? '' : '*'}`}
                placeholder={t('professionalPubli.features.area.placeholder')}
                typesOptions={areaMeasurementTypes}
                name={mainFeaturesIds.SUP_CUBIERTA}
                width={isMobile ? '100%' : '125px'}
                error={t(errors[mainFeaturesIds.SUP_CUBIERTA]?.message)}
                onSelect={value => {
                  dispatch(updateUnitMeasurement('covered', value));
                }}
                currentSelectorValue={getUnitMeasurementName(unitMeasurement.covered)}
                onInputChange={value => {
                  const valueTransformed = transformValue(value);
                  setValue(mainFeaturesIds.SUP_CUBIERTA, valueTransformed);
                }}
                currentValue={coveredArea}
                dataPubli='professionalPubli-coveredSelector'
                tooltipText={t('professionalPubli.features.area.tooltip')}
              />
            ) : null}
          </AreaContainer>
        </StepSectionWrapper>
        {mainData?.formValuesCompleted &&
          !mainData?.formValuesLoading &&
          (!getAntiquityNotApply(mainData?.formValues.propertyType) || formValues['CFT5'] !== '' ? (
            <StepSectionWrapper
              title={`${t('professionalPubli.features.antiquity')}${
                getIsOptionalAntiquity(mainData?.formValues.propertyType) ? '' : '*'
              }`}
              dataPubli='professionalPubli-featuresStep-antiquityContainer'
            >
              <AntiquitySection
                setAntiquityType={v => dispatch(updateAntiquityType(v))}
                antiquityType={antiquityType}
                updateReduxState={value => {
                  clearErrors(mainFeaturesIds.ANTIGUEDAD);
                  setValue(mainFeaturesIds.ANTIGUEDAD, value, { shouldDirty: true });
                  dispatch(updateFormValues({ [mainFeaturesIds.ANTIGUEDAD]: value }));
                }}
                currentAntiquity={formValues[mainFeaturesIds.ANTIGUEDAD]}
                isMobile={isMobile}
                hasError={t(errors[mainFeaturesIds.ANTIGUEDAD]?.message)}
                isDepartment={mainData?.formValues.propertyType === '2'}
                data-publi='professionalPubli-featuresStep-antiquityContainer-antiquitySection'
              />
            </StepSectionWrapper>
          ) : null)}
        {optionsByRealEstateType?.length > 0 ? (
          <StepSectionWrapper
            title={t('professionalPubli.features.rooms')}
            dataPubli='professionalPubli-featuresStep-roomsContainer'
          >
            <RoomsSection
              formValues={formValues}
              updateReduxState={updateCFTValue}
              optionsByRealEstateType={optionsByRealEstateType}
            />
          </StepSectionWrapper>
        ) : null}
      </form>
    </WrapperContainer>
  );
};

export default Features;
